import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LineChart, 
  ArrowLeft, 
  Download, 
  Printer, 
  Users, 
  DollarSign, 
  Eye, 
  Repeat, 
  Loader2
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import '../styles/reachCurveResult.css';

function ReachCurveResult() {
  const navigate = useNavigate();
  
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get and decode calculation ID from URL
  const calculationId = decodeURIComponent(window.location.pathname.split('/').pop());

  useEffect(() => {
    let isCancelled = false;
  
    const fetchResults = async () => {
      if (isCancelled) return;
  
      console.log("Fetching reach results for:", calculationId);
      
      try {
        setLoading(true);
  
        // Extract audience name from the calculation ID
        const parts = calculationId.split('_');
        parts.pop(); // Remove timestamp
        const audienceName = parts.join('-'); // Join back using hyphens
  
        const response = await axios.post(
          'https://n8n.iniai.cloud/webhook/get-reach-result',
          { audienceName },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6',
            }
          }
        );
  
        console.log("Raw API response:", response.data);

        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          // The data is already in the correct format - just use it directly
          const apiData = response.data[0];
          
          // Sort the reach curve points by budget percentage for proper chart display
          if (apiData.reachCurvePoints && Array.isArray(apiData.reachCurvePoints)) {
            apiData.reachCurvePoints.sort((a, b) => a.budgetPercentage - b.budgetPercentage);
          }
          
          // No transformation needed - data is already correctly formatted
          setResultData(apiData);
        } else {
          throw new Error('No results found or invalid data format');
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        setError('Failed to load calculation results. Please try again.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchResults();
  
    return () => {
      isCancelled = true; // Prevent duplicate execution if component unmounts
    };
  }, [calculationId]); 
  

  const handleDownloadCSV = () => {
    if (!resultData) return;

    let csvContent = "data:text/csv;charset=utf-8,";

    // Add reach curve data
    csvContent += "Budget %,Budget Amount,Reach %\n";
    resultData.reachCurvePoints.forEach(point => {
      csvContent += `${point.budgetPercentage},${point.budgetAmount},${point.reachPercentage}\n`;
    });

    // Add channel data
    csvContent += "\nChannel,Budget,Impressions,Reach,Frequency,Reach %\n";
    resultData.channelReachData.forEach(channel => {
      csvContent += `${channel.channelName},${channel.budget},${Math.round(channel.impressions)},${Math.round(channel.reach)},${channel.frequency.toFixed(2)},${channel.reachPercentage.toFixed(2)}\n`;
    });

    // Add summary stats
    csvContent += `\nAudience,${resultData.audienceName}\n`;
    csvContent += `Audience Size,${resultData.audienceSize}\n`;
    csvContent += `Total Budget,${resultData.totalBudget}\n`;
    csvContent += `Net Reach,${Math.round(resultData.netReach)}\n`;
    csvContent += `Net Reach %,${resultData.netReachPercentage.toFixed(2)}\n`;
    csvContent += `Net Frequency,${resultData.netFrequency.toFixed(2)}\n`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `reach_curve_${resultData.audienceName.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleNewCalculation = () => {
    navigate('/reachcurve/planner');
  };

  const handleBackToPlanner = () => {
    navigate('/reachcurve/planner');
  };

  if (loading) {
    return (
      <div className="reach-curve-loading">
        <Loader2 className="loading-icon" />
        <p>Loading calculation results...</p>
      </div>
    );
  }

  if (error || !resultData) {
    return (
      <div className="reach-curve-error">
        <h2>Error</h2>
        <p>{error || 'Failed to load results'}</p>
        <button onClick={handleBackToPlanner} className="back-button">
          <ArrowLeft className="button-icon" />
          Back to Planner
        </button>
      </div>
    );
  }

  return (
    <div className="reach-curve-result">
      <div className="result-header">
        <div className="result-title">
          <LineChart className="result-icon" />
          <h1>Reach Curve Results</h1>
        </div>
        <div className="result-actions">
          <button onClick={handleDownloadCSV} className="action-button">
            <Download className="button-icon" />
            Download CSV
          </button>
          <button onClick={handlePrint} className="action-button">
            <Printer className="button-icon" />
            Print
          </button>
          <button onClick={handleNewCalculation} className="action-button">
            <Repeat className="button-icon" />
            New Calculation
          </button>
        </div>
      </div>

      <div className="campaign-summary">
        <div className="summary-item">
          <Users className="summary-icon" />
          <div className="summary-data">
            <h3>Audience</h3>
            <div className="summary-value">{resultData.audienceName}</div>
            <div className="summary-subvalue">{(resultData.audienceSize || 0).toLocaleString()} people</div>
          </div>
        </div>

        <div className="summary-item">
          <DollarSign className="summary-icon" />
          <div className="summary-data">
            <h3>Budget</h3>
            <div className="summary-value">${(resultData.totalBudget || 0).toLocaleString()}</div>
            <div className="summary-subvalue">Total campaign budget</div>
          </div>
        </div>

        <div className="summary-item">
          <Eye className="summary-icon" />
          <div className="summary-data">
            <h3>Net Reach</h3>
            <div className="summary-value">{Math.round(resultData.netReach || 0).toLocaleString()}</div>
            <div className="summary-subvalue">{(resultData.netReachPercentage || 0).toFixed(2)}% of audience</div>
          </div>
        </div>
      </div>

      {/* Reach Curve Chart */}
      <div className="reach-curve-chart-container">
        <h2>Reach Curve</h2>
        <div className="reach-curve-chart">
        <ResponsiveContainer width="100%" height={400}>
  <RechartsLineChart 
    data={resultData.reachCurvePoints} 
    margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
  >
    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
    <XAxis 
      dataKey="budgetPercentage" 
      tickFormatter={(value) => `${value}%`}
      domain={[0, 100]}
      ticks={[0, 20, 40, 60, 80, 100]}
      label={{ value: 'Budget %', position: 'insideBottom', offset: -5, fill: '#666' }}
    />
    <YAxis 
      tickFormatter={(value) => `${value}%`}
      domain={[0, 100]}
      ticks={[0, 25, 50, 75, 100]}
      label={{ value: 'Reach %', angle: -90, position: 'insideLeft', offset: 10, fill: '#666' }}
    />
    <Tooltip 
      formatter={(value) => [`${value}%`, 'Reach']}
      labelFormatter={(value) => `Budget: ${value}%`}
    />
    <Legend verticalAlign="bottom" height={36} />
    <Line 
      type="monotone" 
      dataKey="reachPercentage" 
      name="Reach %" 
      stroke="#1976D2" 
      strokeWidth={2}
      dot={{ r: 4, strokeWidth: 1, stroke: '#fff', fill: '#1976D2' }}
      activeDot={{ r: 6, stroke: '#fff', strokeWidth: 1 }} 
    />
  </RechartsLineChart>
</ResponsiveContainer>
        </div>
      </div>

      {/* Channel Breakdown Table */}
      <div className="channel-breakdown">
        <h2>Channel Breakdown</h2>
        <table className="channel-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Budget</th>
              <th>Impressions</th>
              <th>Reach</th>
              <th>Frequency</th>
              <th>Reach %</th>
            </tr>
          </thead>
          <tbody>
            {resultData.channelReachData.map((channel, index) => (
              <tr key={index}>
                <td>{channel.channelName}</td>
                <td>${channel.budget.toLocaleString()}</td>
                <td>{Math.round(channel.impressions).toLocaleString()}</td>
                <td>{Math.round(channel.reach).toLocaleString()}</td>
                <td>{channel.frequency.toFixed(2)}</td>
                <td>{channel.reachPercentage.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="result-actions bottom-actions">
        <button onClick={handleBackToPlanner} className="back-button">
          <ArrowLeft className="button-icon" />
          Back to Planner
        </button>
      </div>
    </div>
  );
}

export default ReachCurveResult;